<template>
    <div>
        <div v-if="client">
            <div class="mb-3">
                <h6>Компания: {{ client.company_name }}</h6>
                <p class="text-muted mb-0">ИНН: {{ client.inn }}</p>
            </div>

            <b-form-group label="Статус сотрудничества:" label-for="cooperation-status">
                <b-form-select
                    id="cooperation-status"
                    v-model="form.cooperation_status"
                    :options="statusOptions"
                    required
                ></b-form-select>
            </b-form-group>

            <b-form-group label="Комментарий:" label-for="comment">
                <b-form-textarea
                    id="comment"
                    v-model="form.comment"
                    rows="4"
                    placeholder="Введите комментарий..."
                ></b-form-textarea>
            </b-form-group>

            <b-form-group label="Заметки:" label-for="notes">
                <b-form-textarea
                    id="notes"
                    v-model="form.notes"
                    rows="3"
                    placeholder="Введите заметки..."
                ></b-form-textarea>
            </b-form-group>

            <b-form-group label="Следующий контакт:" label-for="next-contact">
                <b-form-input
                    id="next-contact"
                    type="date"
                    v-model="form.next_contact_at"
                ></b-form-input>
            </b-form-group>

            <div class="d-flex justify-content-end mt-4">
                <b-button
                    variant="secondary"
                    class="mr-2"
                    @click="closeSidebar"
                >
                    Отмена
                </b-button>
                <b-button
                    variant="primary"
                    @click="saveEvaluation"
                    :disabled="isLoading"
                >
                    <span v-if="isLoading">
                        <i class="fas fa-spinner fa-spin mr-1"></i>
                        Сохранение...
                    </span>
                    <span v-else>
                        <i v-if="hasUserChanges" class="fas fa-asterisk text-warning mr-1"></i>
                        Сохранить
                    </span>
                </b-button>
            </div>
        </div>
        
        <!-- Индикатор загрузки -->
        <div v-else-if="isLoadingClient" class="text-center py-4">
            <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
            <p class="mt-2">Загрузка данных клиента...</p>
        </div>
        
        <!-- Сообщение об ошибке -->
        <div v-else-if="loadError" class="text-center py-4">
            <i class="fas fa-exclamation-triangle fa-2x text-danger"></i>
            <p class="mt-2 text-danger">{{ loadError }}</p>
            <b-button variant="outline-primary" @click="loadClientData">
                Попробовать снова
            </b-button>
        </div>
    </div>
</template>

<script>
    import { API_COLD_CLIENTS } from '@/constants/api'

    export default {
        props: {
            value: { type: Boolean, required: true },
            client: { type: Object, required: false, default: null }
        },
        data() {
            return {
                isLoading: false,
                isLoadingClient: false,
                loadError: null,
                hasUserChanges: false, // Флаг для отслеживания изменений пользователя
                isDataLoaded: false, // Флаг для предотвращения повторной загрузки
                form: {
                    cooperation_status: '',
                    comment: '',
                    notes: '',
                    next_contact_at: ''
                }
            }
        },
        computed: {
            // Опции статусов сотрудничества
            statusOptions() {
                return [
                    { value: 'new', text: 'Не обработан' },
                    { value: 'contacted', text: 'Связались' },
                    { value: 'interested', text: 'Заинтересован' },
                    { value: 'not_interested', text: 'Не заинтересован' },
                    { value: 'no_answer', text: 'Не дозвонились' }
                ]
            }
        },
        watch: {
            value(newVal) {
                if (newVal && this.client && this.client.id && !this.isDataLoaded) {
                    this.loadClientData()
                }
            },
            client: {
                handler(newClient) {
                    if (newClient && newClient.id && this.value && !this.isDataLoaded) {
                        this.loadClientData()
                    }
                },
                immediate: true
            },
            // Отслеживаем изменения в форме
            'form.cooperation_status'() {
                this.hasUserChanges = true
            },
            'form.comment'() {
                this.hasUserChanges = true
            },
            'form.notes'() {
                this.hasUserChanges = true
            },
            'form.next_contact_at'() {
                this.hasUserChanges = true
            }
        },
        methods: {
            // Загрузка актуальных данных клиента
            async loadClientData() {
                if (!this.client || !this.client.id || this.isDataLoaded) return
                
                this.isLoadingClient = true
                this.loadError = null
                
                try {
                    const response = await api.call('get', `${API_COLD_CLIENTS}/${this.client.id}`)
                    
                    if (response.data) {
                        // Обновляем данные клиента через emit
                        this.$emit('clientUpdated', response.data)
                        
                        // Сбрасываем флаг изменений только если форма еще не была инициализирована
                        if (!this.hasUserChanges) {
                            this.initializeForm(response.data)
                        }
                        
                        this.isDataLoaded = true // Устанавливаем флаг после успешной загрузки
                    }
                } catch (error) {
                    console.error('Ошибка при загрузке данных клиента:', error)
                    this.loadError = 'Не удалось загрузить данные клиента'
                } finally {
                    this.isLoadingClient = false
                }
            },

            // Инициализация формы
            initializeForm(clientData) {
                if (clientData) {
                    // Если пользователь уже внес изменения, не перезаписываем форму
                    if (this.hasUserChanges) {
                        return
                    }
                    
                    this.form = {
                        cooperation_status: clientData.cooperation_status || 'new',
                        comment: clientData.comment || '',
                        notes: clientData.notes || '',
                        next_contact_at: clientData.next_contact_at ? this.formatDateForInput(clientData.next_contact_at) : ''
                    }
                }
            },

            // Закрыть сайдбар
            closeSidebar() {
                this.$emit('input', false)
                this.loadError = null
                this.hasUserChanges = false // Сбрасываем флаг изменений при закрытии
                this.isDataLoaded = false // Сбрасываем флаг загрузки при закрытии
            },

            // Сохранить оценку
            async saveEvaluation() {
                if (!this.client) return

                this.isLoading = true

                try {
                    const response = await api.call('put', `${API_COLD_CLIENTS}/${this.client.id}`, this.form)

                    if (response.data) {
                        // Эмитим событие успешного сохранения
                        this.$emit('saved', response.data)

                        this.$bvToast.toast('Оценка сохранена успешно', {
                            title: 'Успех',
                            variant: 'success',
                            solid: true
                        })

                        this.hasUserChanges = false // Сбрасываем флаг изменений после сохранения
                        this.closeSidebar()
                    }
                } catch (error) {
                    console.error('Ошибка при сохранении оценки:', error)
                    this.$bvToast.toast('Ошибка при сохранении оценки', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    })
                } finally {
                    this.isLoading = false
                }
            },

            // Форматировать дату для input type="date"
            formatDateForInput(dateString) {
                if (!dateString) return ''
                
                try {
                    let date
                    
                    // Проверяем, если дата в формате DD.MM.YYYY
                    if (typeof dateString === 'string' && dateString.includes('.')) {
                        const parts = dateString.split('.')
                        if (parts.length === 3) {
                            // Создаем дату в формате YYYY-MM-DD
                            const day = parts[0]
                            const month = parts[1]
                            const year = parts[2]
                            date = new Date(`${year}-${month}-${day}`)
                        } else {
                            date = new Date(dateString)
                        }
                    } else {
                        date = new Date(dateString)
                    }
                    
                    // Проверяем, что дата валидна
                    if (isNaN(date.getTime())) {
                        return ''
                    }
                    
                    return date.toISOString().split('T')[0]
                } catch (error) {
                    console.warn('Ошибка форматирования даты:', dateString, error)
                    return ''
                }
            }
        }
    }
</script>

<style scoped>
h6 {
    color: #0d6348;
    font-weight: 600;
}

.text-muted {
    font-size: 0.9rem;
}
</style>
